import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionMenuComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AppointmentListItem } from '../data-access/appointments.models';

@Component({
  selector: 'app-appointment-actions',
  standalone: true,
  imports: [TranslatePipe, ActionMenuComponent],
  template: `
    <lib-action-menu
      buttonAriaLabel="Open appointment actions"
      [disabled]="isBusy || !hasAnyAction"
      [menuTemplate]="actionsMenu"
    />

    <ng-template #actionsMenu let-close="close">
      @if (canConfirmOrCancel) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--subtle"
          (click)="onEdit(close)"
          [disabled]="!canEdit"
        >
          {{ 'action.edit' | t }}
        </button>
      }

      @if (canConfirmOrCancel) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--primary"
          (click)="onConfirm(close)"
          [disabled]="!canConfirm"
        >
          {{ 'action.confirm' | t }}
        </button>
      }

      @if (canCheckInAction) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--primary"
          (click)="onCheckIn(close)"
          [disabled]="!canCheckInAction"
        >
          {{ 'action.checkIn' | t }}
        </button>
      }

      @if (canComplete) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--primary"
          (click)="onComplete(close)"
          [disabled]="!canCompleteAction"
        >
          {{ 'action.finish' | t }}
        </button>
      }

      @if (canNoShowAction) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--danger"
          (click)="onNoShow(close)"
          [disabled]="!canNoShowAction"
        >
          {{ 'action.noShow' | t }}
        </button>
      }

      @if (canConfirmOrCancel) {
        <button
          type="button"
          role="menuitem"
          class="action-menu__item action-menu__item--danger"
          (click)="onCancel(close)"
          [disabled]="!canCancel"
        >
          {{ 'action.cancel' | t }}
        </button>
      }
    </ng-template>
  `,
  styles: [`
    .appointment-actions {
      display: flex;
      gap: 8px;
    }

    .appointment-actions button {
      height: 32px;
      border: 1px solid #d1d5db;
      background: #ffffff;
      border-radius: 8px;
      padding: 0 10px;
      cursor: pointer;
      font-size: 12px;
    }

    .appointment-actions button:disabled {
      opacity: 0.5;
      cursor: default;
    }
  `]
})
export class AppointmentActionsComponent
{
  @Input({ required: true })
  public item!: AppointmentListItem;

  @Input()
  public isBusy = false;

  @Input()
  public canConfirmOrCancel = true;

  @Input()
  public canComplete = true;

  @Input()
  public isClientActor = false;

  @Output()
  public readonly editClicked = new EventEmitter<void>();

  @Output()
  public readonly confirmClicked = new EventEmitter<void>();

  @Output()
  public readonly cancelClicked = new EventEmitter<void>();

  @Output()
  public readonly checkInClicked = new EventEmitter<void>();

  @Output()
  public readonly completeClicked = new EventEmitter<void>();

  @Output()
  public readonly noShowClicked = new EventEmitter<void>();

  protected get hasAnyAction(): boolean {
    return this.canConfirmOrCancel || this.canComplete || this.canNoShowAction;
  }

  protected get canEdit(): boolean {
    return !this.isBusy
      && (this.item.status === 'pending_confirmation' || this.item.status === 'confirmed')
      && !(this.isClientActor && !!this.item.clientChangesLocked);
  }

  protected get canConfirm(): boolean {
    return !this.isBusy && this.item.status === 'pending_confirmation';
  }

  protected get canCheckInAction(): boolean {
    return !this.isBusy
      && this.item.status === 'confirmed'
      && !this.item.checkedInAt
      && !this.item.finishedAt;
  }

  protected get canCompleteAction(): boolean {
    return !this.isBusy
      && this.item.status === 'confirmed'
      && !!this.item.checkedInAt
      && !this.item.finishedAt;
  }

  protected get canCancel(): boolean {
    return !this.isBusy
      && this.item.status !== 'cancelled'
      && this.item.status !== 'completed'
      && this.item.status !== 'no_show';
  }

  protected get canNoShowAction(): boolean {
    return !this.isBusy
      && !this.isClientActor
      && this.item.status === 'confirmed'
      && !this.item.checkedInAt
      && !this.item.finishedAt;
  }

  protected onEdit(close: () => void): void {
    if (!this.canEdit) {
      return;
    }

    close();
    this.editClicked.emit();
  }

  protected onConfirm(close: () => void): void {
    if (!this.canConfirm) {
      return;
    }

    close();
    this.confirmClicked.emit();
  }

  protected onComplete(close: () => void): void {
    if (!this.canCompleteAction) {
      return;
    }

    close();
    this.completeClicked.emit();
  }

  protected onCheckIn(close: () => void): void {
    if (!this.canCheckInAction) {
      return;
    }

    close();
    this.checkInClicked.emit();
  }

  protected onCancel(close: () => void): void {
    if (!this.canCancel) {
      return;
    }

    close();
    this.cancelClicked.emit();
  }

  protected onNoShow(close: () => void): void {
    if (!this.canNoShowAction) {
      return;
    }

    close();
    this.noShowClicked.emit();
  }
}
